import type { RacemapStarter } from '@racemap/utilities/types/events';
import type { ObjectId } from 'bson';
import { useMemo } from 'react';
import type { SWRConfiguration } from 'swr';
import { useEventStarters } from './useEventStarters';

/**
 * Returns a single starter of an event, only updating if that single starter changes.
 *
 * @returns { starter: RacemapStarter | null, isLoading: boolean, error: Error }
 */
export function useEventStarter(
  args: {
    eventId?: string | ObjectId;
    filter?: Array<string>;
    id?: string;
    deviceId?: string;
  },
  options?: SWRConfiguration,
): {
  starter: RacemapStarter | null;
  isLoading: boolean;
  error: Error;
} {
  const { starters, isLoading, error } = useEventStarters(args, options);

  const singleStarter = useMemo(() => {
    if (args.id) {
      const byId = starters?.find((s) => s.id === args.id);
      return byId ?? null;
    }
    if (args.deviceId) {
      const byDevice = starters?.find((s) => s.deviceId === args.deviceId);
      return byDevice ?? null;
    }
    return null;
  }, [starters, args.id, args.deviceId]);

  return { starter: singleStarter, isLoading, error };
}
